import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ChevronRight, Clock, BarChart3, CheckCircle2, ListChecks, Users, Briefcase,
  Video, MonitorPlay, Server, FileText, MessagesSquare, Mic2, Building2,
  Star, GraduationCap, FlaskConical, BadgeCheck, IndianRupee, Globe, Sparkles, Download,
} from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Reveal } from "../components/Reveal";
import { getCourse, getCategory, getCoursesByCategory, INCLUDED, PRICING } from "../data/courses";
import { downloadSyllabus } from "../utils/syllabusPdf";
import { COURSE_PRICES } from "../data/prices";
import { TRAINERS, whatsappLink } from "../data/site";

const ACCENTS = {
  sky: { text: "text-sky-400", border: "border-sky-500/30", bg: "bg-sky-500/10" },
  blue: { text: "text-blue-400", border: "border-blue-500/30", bg: "bg-blue-500/10" },
  orange: { text: "text-orange-400", border: "border-orange-500/30", bg: "bg-orange-500/10" },
  emerald: { text: "text-emerald-400", border: "border-emerald-500/30", bg: "bg-emerald-500/10" },
};

const INCLUDE_ICONS = [Video, MonitorPlay, Server, FileText, MessagesSquare, Mic2];

const buildFaqs = (course, trainer) => [
  {
    q: `I am a fresher. Can I join the ${course.title} course?`,
    a: course.level === "Beginner"
      ? `Yes — ${course.title} is beginner-friendly and starts from absolute basics. Your trainer adjusts the pace to your background in a 1-on-1 setting, so you are never left behind.`
      : `${course.title} is a ${course.level.toLowerCase()}-level program. Ideally you should have: ${course.prerequisites[0].toLowerCase()}. If you're unsure, book a free demo — your trainer will assess and suggest the right starting course first.`,
  },
  {
    q: "What if I miss a live session?",
    a: "No problem. Since training is 1-on-1, sessions are rescheduled at your convenience — no batch pressure. Every session is also recorded with lifetime access, so you can revisit anytime.",
  },
  {
    q: "Do I get lab access and study material?",
    a: `Yes. You get 24x7 access to a dedicated lab pod (${course.labs.length}+ guided labs), trainer-written PDF notes, lab workbooks and cheat sheets — all included in the course fee.`,
  },
  {
    q: `Who will train me for ${course.title}?`,
    a: `Your trainer is ${trainer.name} — ${trainer.role} at ${trainer.company}, with ${trainer.experience} of industry experience. You can meet them in a free demo session before paying anything.`,
  },
  ...(course.exam
    ? [{
        q: "Will this course prepare me for the certification exam?",
        a: `Yes. The syllabus is mapped to the ${course.exam.code} blueprint, and the final week includes timed mock exams with a personal review. Most of our students clear it in the first attempt.`,
      }]
    : [{
        q: "Will I get a certificate after this course?",
        a: "Yes — you receive a LearnWithTAC course completion certificate with a unique certificate ID, plus a portfolio of lab projects you can show in interviews.",
      }]),
  {
    q: "Do you provide job assistance after the course?",
    a: "Yes. Every course includes resume building, LinkedIn optimisation, technical mock interviews with real MNC rubrics, and referral guidance until you're placed.",
  },
];

export default function CourseDetailPage() {
  const { slug } = useParams();
  const course = getCourse(slug);

  if (!course) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center" data-testid="course-not-found">
        <h1 className="font-display text-3xl font-bold text-white">Course not found</h1>
        <p className="mt-3 text-slate-400">The course you're looking for doesn't exist or was moved.</p>
        <Link to="/courses" className="mt-6 inline-block rounded-lg bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm px-6 py-3 transition-colors duration-200">
          Browse All Courses
        </Link>
      </div>
    );
  }

  const cat = getCategory(course.category);
  const a = ACCENTS[cat.accent];
  const trainer = TRAINERS.find((t) => t.id === course.trainer);
  const related = getCoursesByCategory(course.category).filter((c) => c.slug !== slug).slice(0, 3);
  const faqs = buildFaqs(course, trainer);

  const highlights = [
    `1-on-1 live training with ${trainer.name} (${trainer.company})`,
    `${course.hours} of live instructor-led sessions — flexible timing, your pace`,
    "Live class recordings with timestamped notes — revise anytime",
    `24x7 dedicated lab pod — ${course.labs.length}+ guided hands-on labs`,
    "Trainer-written PDF notes, workbooks & cheat sheets",
    "Mock interviews, resume building & placement assistance",
    "Course completion certificate with unique ID",
    "Free demo session before you pay anything",
  ];

  return (
    <>
      <Helmet>
        <title>{`${course.title} Training — 1-on-1 Live | LearnWithTAC`}</title>
        <meta name="description" content={`${course.overview.slice(0, 150)}... Learn ${course.title} 1-on-1 live with ${trainer.name} — recordings, 24x7 labs, notes PDF & mock interviews included.`} />
      </Helmet>

      <section className="relative overflow-hidden" data-testid="course-detail-hero">
        <div className="absolute inset-0 grid-bg" />
        <div className={`absolute -top-24 right-1/4 w-[420px] h-[420px] rounded-full blur-[130px] ${course.category === "cyber" ? "bg-orange-600/15" : "bg-blue-700/20"}`} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
          <Reveal>
            <nav className="flex items-center gap-1.5 text-xs text-slate-500 mb-6 font-mono2" data-testid="course-breadcrumb">
              <Link to="/" className="hover:text-sky-400 transition-colors duration-200">Home</Link>
              <ChevronRight className="w-3 h-3" />
              <Link to="/courses" className="hover:text-sky-400 transition-colors duration-200">Courses</Link>
              <ChevronRight className="w-3 h-3" />
              <span className={a.text}>{cat.name}</span>
            </nav>
            <div className="flex flex-wrap items-center gap-2.5 mb-5">
              {course.tag && (
                <span className={`font-mono2 text-[10px] uppercase tracking-[0.2em] ${a.text} ${a.bg} border ${a.border} rounded-full px-3 py-1 font-semibold`}>
                  {course.tag}
                </span>
              )}
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-400 border border-sky-500/20 rounded-full px-3 py-1">
                <BarChart3 className="w-3.5 h-3.5 text-orange-500" /> {course.level}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs text-slate-400 border border-sky-500/20 rounded-full px-3 py-1">
                <Clock className="w-3.5 h-3.5 text-orange-500" /> {course.duration} • {course.hours} • 1-on-1 Live
              </span>
            </div>
            <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight max-w-3xl">
              {course.title}
            </h1>
            <p className="mt-5 max-w-3xl text-base sm:text-lg text-slate-300 leading-relaxed">{course.overview}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              {[
                { icon: GraduationCap, label: "Industry-Expert Trainer" },
                { icon: Star, label: "4.9/5 Student Rating" },
                { icon: Briefcase, label: "Placement Assistance" },
                { icon: Server, label: "24x7 Hands-on Labs" },
              ].map(({ icon: Icon, label }) => (
                <span key={label} className="inline-flex items-center gap-2 rounded-full glass-card border border-sky-500/20 px-4 py-2 text-xs font-medium text-slate-300">
                  <Icon className="w-3.5 h-3.5 text-orange-500" /> {label}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24">
        <div className="grid lg:grid-cols-[1.6fr_1fr] gap-10 items-start">
          <div className="space-y-14">
            <Reveal data-testid="course-highlights">
              <h2 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <Sparkles className={`w-6 h-6 ${a.text}`} /> Training Highlights
              </h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {highlights.map((h) => (
                  <li key={h} className="flex items-start gap-3 rounded-xl border border-sky-500/15 bg-[#0B1226] px-4 py-3.5 text-sm text-slate-300">
                    <BadgeCheck className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" /> {h}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal data-testid="course-syllabus">
              <h2 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <ListChecks className={`w-6 h-6 ${a.text}`} /> Course Content
              </h2>
              <Accordion type="single" collapsible defaultValue="mod-0" className="space-y-3">
                {course.modules.map((m, i) => {
                  const [head, ...rest] = m.split(" — ");
                  return (
                    <AccordionItem key={i} value={`mod-${i}`} data-testid={`syllabus-item-${i}`} className={`rounded-xl border ${a.border} bg-[#0B1226] px-6`}>
                      <AccordionTrigger className="font-display text-sm sm:text-base font-semibold text-white hover:no-underline py-4 text-left">
                        <span className="flex items-center gap-3">
                          <span className={`font-mono2 text-xs ${a.text}`}>{String(i + 1).padStart(2, "0")}</span>
                          {head}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="text-sm text-slate-400 leading-relaxed pb-4 pl-9">
                        {rest.join(" — ") || head}
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
              </Accordion>
            </Reveal>

            <Reveal data-testid="course-topics">
              <h2 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <CheckCircle2 className={`w-6 h-6 ${a.text}`} /> What You Will Learn — Topic by Topic
              </h2>
              <div className="rounded-2xl border border-sky-500/20 bg-[#0B1226] p-6 sm:p-8">
                <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
                  {course.topics.map((t) => (
                    <li key={t} className="flex items-start gap-2.5 text-sm text-slate-300">
                      <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${course.category === "cyber" ? "bg-orange-400" : course.category === "cloud" ? "bg-blue-400" : "bg-sky-400"}`} />
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>

            <Reveal data-testid="course-labs">
              <h2 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <FlaskConical className={`w-6 h-6 ${a.text}`} /> Hands-on Labs You Will Perform
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {course.labs.map((l, i) => (
                  <div key={l} className={`rounded-xl border ${a.border} bg-[#0B1226] p-5 hover:-translate-y-1 transition-transform duration-300`}>
                    <span className={`font-mono2 text-xs font-bold ${a.text}`}>LAB {String(i + 1).padStart(2, "0")}</span>
                    <p className="mt-2 text-sm text-slate-300 leading-relaxed">{l}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            {course.exam && (
              <Reveal data-testid="course-exam">
                <h2 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-3">
                  <GraduationCap className={`w-6 h-6 ${a.text}`} /> Certification Exam Details
                </h2>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                  {[
                    { label: "Exam Code", value: course.exam.code },
                    { label: "Duration", value: course.exam.duration },
                    { label: "Format", value: course.exam.questions },
                    { label: "Validity", value: course.exam.validity },
                    { label: "Exam Fee", value: course.exam.cost },
                  ].map((e) => (
                    <div key={e.label} className="rounded-xl border border-sky-500/20 bg-[#0B1226] p-4 text-center">
                      <p className="font-mono2 text-[10px] uppercase tracking-[0.15em] text-slate-500">{e.label}</p>
                      <p className="mt-1.5 font-display text-sm font-bold text-white leading-snug">{e.value}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            )}

            <Reveal data-testid="course-prerequisites">
              <h2 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <CheckCircle2 className={`w-6 h-6 ${a.text}`} /> Prerequisites
              </h2>
              <ul className="grid sm:grid-cols-2 gap-3">
                {course.prerequisites.map((p) => (
                  <li key={p} className="flex items-start gap-3 rounded-xl border border-sky-500/15 bg-[#0B1226] px-4 py-3.5 text-sm text-slate-300">
                    <CheckCircle2 className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal data-testid="course-audience">
              <h2 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <Users className={`w-6 h-6 ${a.text}`} /> Who Can Do This Course
              </h2>
              <ul className="space-y-3">
                {course.audience.map((w) => (
                  <li key={w} className="flex items-start gap-3 text-sm sm:text-base text-slate-300">
                    <span className={`w-1.5 h-1.5 rounded-full ${course.category === "cyber" ? "bg-orange-400" : course.category === "cloud" ? "bg-blue-400" : "bg-sky-400"} mt-2 shrink-0`} /> {w}
                  </li>
                ))}
              </ul>
            </Reveal>

            <Reveal data-testid="course-jobs">
              <h2 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <Briefcase className={`w-6 h-6 ${a.text}`} /> Career Opportunities & Salary
              </h2>
              <div className="flex flex-wrap gap-3 mb-5">
                <span className="inline-flex items-center gap-2 rounded-full bg-orange-500/10 border border-orange-500/30 px-4 py-2 text-sm font-semibold text-orange-300">
                  <IndianRupee className="w-4 h-4" /> India: {course.salary.india}
                </span>
                <span className="inline-flex items-center gap-2 rounded-full bg-sky-500/10 border border-sky-500/30 px-4 py-2 text-sm font-semibold text-sky-300">
                  <Globe className="w-4 h-4" /> Global: {course.salary.global}
                </span>
              </div>
              <div className="grid sm:grid-cols-3 gap-4">
                {course.jobs.map((j) => (
                  <div key={j} className={`rounded-xl border ${a.border} bg-[#0B1226] p-5 text-center hover:-translate-y-1 transition-transform duration-300`}>
                    <Briefcase className={`w-5 h-5 ${a.text} mx-auto mb-2.5`} />
                    <p className="font-display text-sm font-semibold text-white">{j}</p>
                  </div>
                ))}
              </div>
            </Reveal>

            <Reveal data-testid="course-faqs">
              <h2 className="font-display text-2xl font-bold text-white mb-6 flex items-center gap-3">
                <MessagesSquare className={`w-6 h-6 ${a.text}`} /> Frequently Asked Questions
              </h2>
              <Accordion type="single" collapsible className="space-y-3">
                {faqs.map((f, i) => (
                  <AccordionItem key={i} value={`faq-${i}`} data-testid={`course-faq-${i}`} className="rounded-xl border border-sky-500/20 bg-[#0B1226] px-6 data-[state=open]:border-orange-500/50 transition-colors duration-300">
                    <AccordionTrigger className="font-display text-sm sm:text-base font-semibold text-white hover:text-orange-400 hover:no-underline py-4 text-left">
                      {f.q}
                    </AccordionTrigger>
                    <AccordionContent className="text-sm text-slate-400 leading-relaxed pb-4">
                      {f.a}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </Reveal>

            <Reveal data-testid="course-counsellor-strip">
              <div className="rounded-2xl border border-orange-500/40 bg-gradient-to-r from-orange-500/15 via-[#0B1226] to-blue-700/20 p-7 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center gap-5">
                <div className="flex-1">
                  <h3 className="font-display text-xl font-bold text-white">
                    Still have doubts about <span className="text-gradient-orange">{course.title}</span>?
                  </h3>
                  <p className="mt-1.5 text-sm text-slate-300">
                    Talk to a counsellor or book a free 1-on-1 demo with {trainer.name.split(",")[0]} — no commitment.
                  </p>
                </div>
                <div className="flex gap-3 shrink-0">
                  <a
                    data-testid="course-counsellor-whatsapp"
                    href={whatsappLink(`Hi LearnWithTAC, I have questions about the ${course.title} course.`)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center rounded-xl bg-[#25D366] hover:bg-[#1fb857] text-[#060913] font-display font-bold text-sm px-5 py-3 transition-colors duration-200"
                  >
                    WhatsApp Us
                  </a>
                </div>
              </div>
            </Reveal>
          </div>

          <aside className="lg:sticky lg:top-24 space-y-6">
            <Reveal data-testid="course-included-card">
              <div className="rounded-2xl border border-sky-500/25 bg-[#0B1226] p-6">
                <h3 className="font-display text-lg font-bold text-white mb-5">Every Course Includes</h3>
                <ul className="space-y-3.5">
                  {INCLUDED.map((inc, i) => {
                    const Icon = INCLUDE_ICONS[i];
                    return (
                      <li key={inc} className="flex items-center gap-3 text-sm text-slate-300">
                        <span className={`w-8 h-8 rounded-lg ${a.bg} border ${a.border} flex items-center justify-center shrink-0`}>
                          <Icon className={`w-4 h-4 ${a.text}`} />
                        </span>
                        {inc}
                      </li>
                    );
                  })}
                </ul>
                <div className="mt-6 rounded-xl border border-orange-500/30 bg-orange-500/5 p-4 text-center" data-testid="course-price-card">
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-slate-500">1-on-1 Course Fee (USD)</p>
                  <p className="font-display text-2xl font-extrabold text-white mt-1">{COURSE_PRICES[course.slug] || "Coming Soon"}</p>
                  <p className="text-[11px] text-slate-500 mt-1">{COURSE_PRICES[course.slug] ? "One-time • Live 1-on-1 classes, labs, notes & recordings included" : "Exact fee WhatsApp par puchiye — EMI options available"}</p>
                </div>
                <button
                  data-testid="course-download-syllabus-btn"
                  onClick={() => downloadSyllabus(course, trainer, cat, COURSE_PRICES[course.slug] || "Coming Soon - ask on WhatsApp")}
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 rounded-xl border border-orange-500/50 text-orange-400 hover:bg-orange-500/10 font-display font-semibold text-sm px-6 py-3 transition-colors duration-200"
                >
                  <Download className="w-4 h-4" /> Download Syllabus PDF
                </button>
                <a
                  data-testid="course-enroll-whatsapp-btn"
                  href={whatsappLink(`Hi LearnWithTAC, I want to enroll in the ${course.title} 1-on-1 program. Please share pricing and a free demo slot.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#25D366] hover:bg-[#1fb857] text-[#060913] font-display font-bold text-sm px-6 py-3.5 transition-all duration-300 hover:-translate-y-0.5 shadow-[0_0_30px_rgba(37,211,102,0.25)]"
                >
                  Enroll via WhatsApp
                </a>
              </div>
            </Reveal>

            <Reveal delay={0.1} data-testid="course-trainer-card">
              <div className={`rounded-2xl border ${a.border} bg-[#0B1226] overflow-hidden`}>
                <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-slate-500 px-6 pt-5">Your Trainer</p>
                <div className="p-6 pt-4 flex items-center gap-4">
                  <img src={trainer.image} alt={trainer.name} className="w-16 h-16 rounded-xl object-cover object-top border border-sky-500/30" />
                  <div>
                    <p className="font-display text-base font-bold text-white">{trainer.name}</p>
                    <p className="text-xs text-orange-400 font-medium">{trainer.role}</p>
                    <p className="flex items-center gap-1.5 text-[11px] text-slate-400 mt-1">
                      <Building2 className="w-3 h-3 text-sky-400" /> {trainer.company}
                    </p>
                  </div>
                </div>
                <div className="px-6 pb-6 text-xs text-slate-400 space-y-1.5">
                  <p>{trainer.experience} — {trainer.detail}</p>
                  <p className="text-slate-500">{trainer.specialization}</p>
                </div>
              </div>
            </Reveal>
          </aside>
        </div>

        {related.length > 0 && (
          <Reveal className="mt-20" data-testid="related-courses">
            <h2 className="font-display text-2xl font-bold text-white mb-6">
              More in <span className={a.text}>{cat.name}</span>
            </h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {related.map((r) => (
                <Link
                  key={r.slug}
                  to={`/courses/${r.slug}`}
                  data-testid={`related-course-${r.slug}`}
                  className={`group rounded-2xl border ${a.border} bg-[#0B1226] p-6 hover:-translate-y-1.5 hover:shadow-[0_0_25px_rgba(56,189,248,0.12)] transition-all duration-300`}
                >
                  <h3 className="font-display text-base font-semibold text-white group-hover:text-sky-300 transition-colors duration-200">{r.title}</h3>
                  <p className="text-xs text-slate-500 mt-2">{r.level} • {r.duration} • 1-on-1 Live</p>
                  <span className={`mt-4 inline-flex items-center gap-1 text-xs font-semibold ${a.text}`}>
                    View details <ChevronRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-300" />
                  </span>
                </Link>
              ))}
            </div>
          </Reveal>
        )}
      </section>
    </>
  );
}
